import React from "react";
import { fmtNumber } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Phone, Mail, CheckSquare, CalendarClock, StickyNote, Users } from "lucide-react";

const TYPES = [
  { key: "calls", label: "Calls", icon: Phone },
  { key: "emails", label: "Emails", icon: Mail },
  { key: "tasks", label: "Tasks", icon: CheckSquare },
  { key: "meetings", label: "Meetings", icon: CalendarClock },
  { key: "notes", label: "Notes", icon: StickyNote },
];

export default function ActivitySnapshot({ activity, rangeLabel }) {
  const byType = activity?.by_type || [];
  const byRep = activity?.by_rep || [];
  const byStatus = activity?.by_status || [];
  const total = activity?.total || 0;

  const countFor = (key) => byType.find((t) => t.type === key)?.count ?? 0;
  const labelFor = (key) => TYPES.find((t) => t.key === key)?.label ?? key;

  return (
    <Card data-testid="activity-snapshot">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" />
          Sales Rep Activity Snapshot
          <span className="text-xs font-normal text-muted-foreground ml-1">· {rangeLabel}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Summary tiles by type */}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="activity-summary">
          {TYPES.map(({ key, label, icon: Icon }) => (
            <div
              key={key}
              className="rounded-lg border border-border bg-muted/40 px-3 py-3 text-center"
              data-testid={`activity-total-${key}`}
            >
              <Icon className="w-4 h-4 text-primary mx-auto mb-1" />
              <p className="text-xl font-bold text-foreground">{fmtNumber(countFor(key))}</p>
              <p className="text-[11px] text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>

        {/* Per-rep breakdown */}
        {total === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-6">
            No activities in this period. (Activity data requires a live HubSpot connection
            with engagement read scopes — CSV uploads have none.)
          </div>
        ) : (
          <div className="max-h-[280px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rep</TableHead>
                  {TYPES.map((t) => (
                    <TableHead key={t.key} className="text-right">
                      {t.label}
                    </TableHead>
                  ))}
                  <TableHead className="text-right font-semibold">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {byRep.map((r) => (
                  <TableRow key={r.owner_id}>
                    <TableCell className="font-medium text-foreground">{r.name}</TableCell>
                    {TYPES.map((t) => (
                      <TableCell key={t.key} className="text-right text-muted-foreground">
                        {fmtNumber(r[t.key] ?? 0)}
                      </TableCell>
                    ))}
                    <TableCell className="text-right font-semibold text-foreground">
                      {fmtNumber(r.total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Status breakdown (all statuses shown separately) */}
        {byStatus.length > 0 && (
          <div data-testid="activity-status-breakdown">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Status breakdown
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {byStatus.map((grp) => (
                <div
                  key={grp.type}
                  className="rounded-lg border border-border p-3"
                  data-testid={`activity-status-${grp.type}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-foreground">{labelFor(grp.type)}</span>
                    <span className="text-xs text-muted-foreground">{fmtNumber(grp.total)} total</span>
                  </div>
                  <div className="space-y-1">
                    {grp.statuses.map((s) => (
                      <div key={s.status} className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{s.status}</span>
                        <span className="font-medium text-foreground">{fmtNumber(s.count)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
